import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import useT from "../hooks/useT"
import PlatformSelector from "./PlatformSelector"
import { lineKey, lineProduct, linesNeedingPlatform } from "./cartItems"

/**
 * Checkout step "platform": every license line added without a platform
 * distribution (e.g. from a /buy-book link) picks its platforms here. The
 * selection is written to the cart line's options ({ platform, licenses })
 * and its quantity, so the order payload and the summary carry it.
 */
export default function PlatformStep({ store, isActive }) {
  const t = useT()
  const lines = linesNeedingPlatform(store.cart)
  const [selection, setSelection] = useState({})

  const allValid = lines.length > 0 && lines.every((line) => selection[lineKey(line)]?.valid)

  const apply = () => {
    for (const line of lines) {
      const picked = selection[lineKey(line)]
      if (!picked) continue
      store.setItemOptions(lineKey(line), { platform: picked.platform, licenses: picked.licenses, universal: picked.universal })
      store.setQuantity(lineKey(line), picked.licenses)
    }
    PubSub.publish("PURCHASE_FLOW_NEXT", { stepData: { platforms: selection } })
  }

  useEffect(() => {
    if (!isActive) return
    PubSub.publish("PURCHASE_FLOW_UPDATE_BUTTON", { stepId: "platform", canContinue: allValid, loading: false, onContinue: apply })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, allValid, selection])

  // Nothing left to pick (e.g. the last line was removed): move on
  useEffect(() => {
    if (isActive && lines.length === 0) PubSub.publish("PURCHASE_FLOW_REFRESH")
  }, [isActive, lines.length])

  return (
    <div className="flex flex-col gap-8 px-4 py-6" data-testid="checkout-platform">
      <p className="m-0 text-center text-[15px] text-text_secondary">{t`Choose the platforms and the number of licenses you need.`}</p>
      {lines.map((line) => (
        <PlatformSelector
          key={lineKey(line)}
          product={lineProduct(line)}
          initialQuantity={line.quantity}
          onChange={(picked) => setSelection((s) => ({ ...s, [lineKey(line)]: picked }))}
        />
      ))}
    </div>
  )
}
